import { round } from 'lodash-es'

const combinePointLoadsByLoadCaseAndPosition = (pointLoads: PointLoadWithDomain[]) => {
  // Create a map to store combined loads
  const combinedLoadsMap = new Map<string, PointLoadWithDomain & { originalLoadGuids?: string[] }>()
  let combinedLoadIndex = 1

  pointLoads.forEach(load => {
    const roundedRelativePosition = round(load.relative_position, 2)
    // Create a unique key based on load case type, domain, and relative position
    const key = `${load.load_case.load_case_type}-${
      load.load_case.reference_load_case_type ?? ''
    }-${load.domain.guid}-${roundedRelativePosition}`

    if (combinedLoadsMap.has(key)) {
      // If we already have a load at this position with the same domain, combine the forces
      const existingLoad = combinedLoadsMap.get(key)!
      const combinedForce = {
        x: existingLoad.force.x + load.force.x,
        y: existingLoad.force.y + load.force.y,
        z: existingLoad.force.z + load.force.z,
      }

      // Collect original load guids
      const originalGuids = existingLoad.originalLoadGuids || [existingLoad.guid]
      originalGuids.push(load.guid)

      combinedLoadsMap.set(key, {
        ...existingLoad,
        force: combinedForce,
        relative_position: roundedRelativePosition,
        guid: `Combined-Point-Load-${combinedLoadIndex}`,
        comment: `Combined-Point-Load`,
        originalLoadGuids: originalGuids,
      })
    } else {
      // If this is the first load at this position, add it to the map
      combinedLoadsMap.set(key, load)
    }
    combinedLoadIndex++
  })

  // Convert the map values back to an array
  return Array.from(combinedLoadsMap.values())
}

const interpolateForce = (load: LineLoadWithDomain, position: number): Force => {
  const { relative_start, relative_end, start, end } = load
  const roundedRelativeStart = round(relative_start, 2)
  const roundedRelativeEnd = round(relative_end, 2)
  const totalLength = roundedRelativeEnd - roundedRelativeStart
  const positionInLoad = position - roundedRelativeStart
  const ratio = positionInLoad / totalLength

  return {
    x: start.x + (end.x - start.x) * ratio,
    y: start.y + (end.y - start.y) * ratio,
    z: start.z + (end.z - start.z) * ratio,
  }
}

const combineLineLoadsByLoadCaseAndPosition = (lineLoads: LineLoadWithDomain[]) => {
  // Group loads by load case type and domain
  const loadsByCaseAndDomain = new Map<string, LineLoadWithDomain[]>()
  lineLoads.forEach(load => {
    const key = `${load.load_case.load_case_type}-${
      load.load_case.reference_load_case_type ?? ''
    }-${load.domain.guid}`
    if (!loadsByCaseAndDomain.has(key)) {
      loadsByCaseAndDomain.set(key, [])
    }
    loadsByCaseAndDomain.get(key)!.push(load)
  })

  const result: (LineLoadWithDomain & { originalLoadGuids?: string[] })[] = []
  let combinedLoadIndex = 1

  // Process each load case and domain group separately
  loadsByCaseAndDomain.forEach(loads => {
    // Create a list of all interval boundaries
    const boundaries = new Set<number>()
    loads.forEach(load => {
      boundaries.add(round(load.relative_start, 2))
      boundaries.add(round(load.relative_end, 2))
    })
    const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b)

    // Create intervals between boundaries
    for (let i = 0; i < sortedBoundaries.length - 1; i++) {
      const intervalStart = sortedBoundaries[i]
      const intervalEnd = sortedBoundaries[i + 1]

      // Find all loads that overlap with this interval
      const overlappingLoads = loads.filter(
        load =>
          round(load.relative_start, 2) <= intervalStart &&
          round(load.relative_end, 2) >= intervalEnd,
      )

      if (overlappingLoads.length > 0) {
        // Calculate the combined forces for this interval by interpolating each load's force
        const combinedStartForce = { x: 0, y: 0, z: 0 }
        const combinedEndForce = { x: 0, y: 0, z: 0 }

        // Collect original load guids from all overlapping loads
        const originalLoadGuids = overlappingLoads.map(load => load.guid)

        overlappingLoads.forEach(load => {
          // Interpolate the force at the start and end of this interval
          const startForce = interpolateForce(load, intervalStart)
          const endForce = interpolateForce(load, intervalEnd)

          // Add the interpolated forces
          combinedStartForce.x += startForce.x
          combinedStartForce.y += startForce.y
          combinedStartForce.z += startForce.z
          combinedEndForce.x += endForce.x
          combinedEndForce.y += endForce.y
          combinedEndForce.z += endForce.z
        })

        // Create a new combined load for this interval
        const baseLoad = overlappingLoads[0]
        result.push({
          ...baseLoad,
          relative_start: intervalStart,
          relative_end: intervalEnd,
          start: combinedStartForce,
          end: combinedEndForce,
          guid: `Combined-Line-Load-${combinedLoadIndex}`,
          comment: `Combined-Line-Load`,
          originalLoadGuids,
        })
        combinedLoadIndex++
      }
    }
  })

  return result
}

const combineAllLoadsByLoadCaseAndPosition = (loads: (AreaLoad | DomainLoad)[]) => {
  const pointLoads = loads.filter(load => load.load_type === 'point-load') as PointLoadWithDomain[]
  const lineLoads = loads.filter(load => load.load_type === 'line-load') as LineLoadWithDomain[]
  const areaLoads = loads.filter(load => load.load_type === 'area-load') as AreaLoad[]
  const combinedPointLoads = combinePointLoadsByLoadCaseAndPosition(pointLoads)
  const combinedLineLoads = combineLineLoadsByLoadCaseAndPosition(lineLoads)
  return [...combinedPointLoads, ...combinedLineLoads, ...areaLoads]
}

export {
  combinePointLoadsByLoadCaseAndPosition,
  combineLineLoadsByLoadCaseAndPosition,
  combineAllLoadsByLoadCaseAndPosition,
}
